// Helpers for `FieldSpec` settings (a data source's per-step fields, a signal
// data type's trigger settings): what a fresh one starts with, and whether a
// value is filled in and in range. The editor's validation and the inputs
// judge values the same way through these.

import type { FieldSpec, PropertyCondition } from "../../api/client.ts";

// Condition ops that test the property itself and take no value.
export const NO_VALUE_OPS: ReadonlySet<string> = new Set(["is_empty", "is_not_empty"]);

/** What a freshly added source starts with: each field's default, else null. */
export function fieldDefaults(fields: FieldSpec[]): Record<string, unknown> {
  return Object.fromEntries(fields.map((f) => [f.name, f.default ?? null]));
}

/** Whether a required field counts as not filled in. */
export function isFieldMissing(field: FieldSpec, value: unknown): boolean {
  if (value === undefined || value === null) return true;
  switch (field.type) {
    case "number":
      // Money: a cap of 0 is no cap.
      return typeof value !== "number" || !(value > 0);
    case "integer":
      return typeof value !== "number" || !Number.isFinite(value);
    case "string":
      return typeof value !== "string" || !value.trim();
    case "boolean":
      return typeof value !== "boolean";
    case "string_list":
    case "choice_list":
    case "conditions":
      return !Array.isArray(value) || value.length === 0;
    default:
      return false;
  }
}

export function isFieldOutOfRange(field: FieldSpec, value: unknown): boolean {
  return (
    typeof value === "number" &&
    ((field.minimum != null && value < field.minimum) || (field.maximum != null && value > field.maximum))
  );
}

export function rangeText(field: FieldSpec): string {
  if (field.minimum != null && field.maximum != null) {
    return `between ${field.minimum} and ${field.maximum}`;
  }
  return field.minimum != null ? `at least ${field.minimum}` : `at most ${field.maximum}`;
}

/** A conditions field's rows, whatever was stored. */
export function conditionsOf(value: unknown): PropertyCondition[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((c): c is Record<string, unknown> => typeof c === "object" && c !== null)
    .map((c) => ({
      property: typeof c.property === "string" ? c.property : "",
      op: typeof c.op === "string" ? c.op : "",
      value: typeof c.value === "string" ? c.value : c.value == null ? "" : String(c.value),
    }));
}

/** A row without a property or op, or without the value its op needs. */
export function isConditionIncomplete(c: PropertyCondition): boolean {
  return !c.property.trim() || !c.op || (!NO_VALUE_OPS.has(c.op) && !c.value.trim());
}
